import React, {useCallback, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {Add} from "@mui/icons-material";
import {Box, SvgIcon, Tooltip} from "@mui/material";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState,} from "../../../../../../../../packages/data-grid";
import {RegionRatesListDataGridColumnNames} from "../../../../../../../../core/constants/table/data-grid-column-keys";
import Button from "../../../../../../../components/base/button/button";
import SearchInput from "../../../../../../../components/base/input/search_input";
import PartnerDataGrid from "../../../../../../../containers/data-grid";
import UpsertRegionRateDialog from "../../../../../../../components/dialogs/upsert-region-rate-dialog";
import Api from "../../../../../../../../core/services/api/api";
import useSearch from "../../../../../../../hooks/use-search";
import useAccessLevels from "../../../../../../../hooks/use-access-levels";
import {useAppDispatch} from "../../../../../../../redux";
import {setReduxDialogState} from "../../../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../../../core/models/static/routes/redux-store-routes";
import IconButton from "../../../../../../../components/base/button/icon_button";
import {ReactComponent as RemoveIcon} from "../../../../../../../../assets/images/remove.svg";


const initUpsertDialogState = {open: false, data: null};

const dataGridSaveConfig = {
    name: 'RegionRatesListViewDataGrid',
    version: 1,
};

/**
 * Region rates list panel: search, grid, add dialog, and permission-gated remove.
 */
const RegionRatesSection = () => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.shipping-fees.region-rates'});
    const operatorAccessLevels = useAccessLevels();
    const dispatch = useAppDispatch();
    const [data, setData] = useState([]);
    const [upsertDialogState, setUpsertDialogState] = useState(initUpsertDialogState);
    const isMounted = useIsMounted();
    /**@type {React.MutableRefObject<DataGridController>}*/
    const dataGridControllerRef = useRef();

    const columns = useMemo(() =>
            /** @type {DataGridColumn[]}*/
            [
                {
                    title: t('columns.region'),
                    name: RegionRatesListDataGridColumnNames.region,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    sortable: false,
                },
                {
                    title: t('columns.rate'),
                    name: RegionRatesListDataGridColumnNames.rate,
                    alignment: DataGridColumnAlignments.right,
                    type: DataGridColumnTypes.money,
                    sortable: false,
                },
                ...(operatorAccessLevels.packages.sales.manageRegionRates ? [{
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    name: RegionRatesListDataGridColumnNames.remove,
                    pinned: true,
                    resizable: false,
                    visibilityToggleable: false,
                    sortable: false,
                    width: 0,
                }] : []),
            ],
        [operatorAccessLevels.packages.sales.manageRegionRates, t]);

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const initialSearchDataSettings = useMemo(() => ({
        filters: {keyword: ''},
    }), []);

    /**
     * Fetches region rates from the server.
     *
     * @type {(function(UseSearchFunctionArg): Promise<void>)}
     */
    const fetchEntries = useCallback(async (args) => {
        const response = await Api.listRegionRates({
            keyword: args.filters?.keyword ?? '',
        }, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.aborted)
            return;
        if (response?.resultFlag) {
            setData(response.data ?? []);
        }
    }, [isMounted]);

    /**
     * Searches region rates based on keyword filter.
     *
     * @type {UseSearchFunction}
     */
    const searchEntries = useCallback(async (result) => {
        await fetchEntries(result);
    }, [fetchEntries]);

    const searchData = useSearch(searchEntries, initialSearchDataSettings);

    /**
     * Updates keyword filter and triggers search.
     *
     * @type {function(string): void}
     */
    const searchWithKeywords = useCallback((keyword) => {
        searchData.setFilters({
            keyword: keyword?.trim(),
        });
    }, [searchData]);

    /**
     * Opens the upsert dialog for creating a new region rate.
     */
    const onAddRegionRateClicked = useCallback(() => {
        setUpsertDialogState({open: true, data: null});
    }, []);

    /**
     * Removes a region rate by its original region id.
     *
     * @type {function(number): Promise<void>}
     */
    const removeRegionRate = useCallback(async (originalRegionId) => {
        searchData.setLoading(true);
        const response = await Api.removeRegionRate({regionOriginalIds: [originalRegionId]});
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(prevState => prevState.filter(e => e.originalRegionId !== originalRegionId));
        }
        searchData.setLoading(false);
    }, [isMounted, searchData]);

    /**
     * Opens the confirmation dialog before removing a region rate.
     *
     * @type {function(number): void}
     */
    const getRemoveConfirmation = useCallback((originalRegionId) => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.confirmation, {
            open: true,
            title: t('removeConfirmationTitle'),
            description: t('removeConfirmationDescription'),
            confirmButtonText: t('removeConfirm'),
            cancelButtonText: t('cancel'),
            maxWidth: 'sm',
            fullWidth: false,
            onClose: async (confirmed) => {
                if (confirmed)
                    await removeRegionRate(originalRegionId);
                return true;
            },
        }));
    }, [dispatch, removeRegionRate, t]);

    /**
     * Adds or updates a region rate in the local list after a successful upsert.
     *
     * @type {function(*): void}
     */
    const onRegionRateUpserted = useCallback((regionRate) => {
        setData(prevState => {
            const foundIndex = prevState.findIndex(e => e.originalRegionId === regionRate.originalRegionId);
            if (foundIndex === -1)
                return [regionRate, ...prevState];
            return prevState.map(e => e.originalRegionId === regionRate.originalRegionId ? {...e, ...regionRate} : e);
        });
    }, []);

    /**
     * Builds data grid row entities from the fetched data.
     */
    const dataGridRows = useMemo(() => data?.map(regionRate => ({
        key: regionRate.originalRegionId ?? regionRate.id,
        data: regionRate,
        cells: {
            [RegionRatesListDataGridColumnNames.region]: regionRate.originalRegionTitle,
            [RegionRatesListDataGridColumnNames.rate]: regionRate.amount,
            ...(operatorAccessLevels.packages.sales.manageRegionRates && {
                [RegionRatesListDataGridColumnNames.remove]:
                    <Tooltip title={t('removeTooltip')}>
                        <IconButton
                            onPressed={() => getRemoveConfirmation(regionRate.originalRegionId)}
                            color={'paper.error'}
                            className={'remove-icon'}
                            disabled={searchData.loading}
                        >
                            <SvgIcon component={RemoveIcon} className='icon normal'/>
                        </IconButton>
                    </Tooltip>,
            }),
        },
    })), [data, getRemoveConfirmation, operatorAccessLevels.packages.sales.manageRegionRates, searchData.loading, t]);

    return (
        <>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 4}}>
                {
                    operatorAccessLevels.packages.sales.manageRegionRates &&
                    <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
                        <Button
                            className={'d-flex align-items-center justify-content-center'}
                            variant={'outlined'}
                            color={'primary'}
                            onClick={onAddRegionRateClicked}
                        >
                            <Add className='icon small mr-2'/>
                            <p className={'button-label'}>{t('addButton')}</p>
                        </Button>
                    </Box>
                }
                <SearchInput
                    loading={searchData.loading}
                    initialValue={searchData.filters?.keyword}
                    autoSearch
                    immediateSearch
                    search={searchWithKeywords}
                    label={t('searchLabel')}
                />
                <PartnerDataGrid
                    ref={dataGridControllerRef}
                    {...dataGridSaveConfig}
                    initialLayoutState={{
                        columns: columns,
                    }}
                    columns={dataGridColumns}
                    rows={dataGridRows}
                    hidePagination
                    hidePageSize
                    loading={searchData.loading}
                    state={{
                        loading: {state: searchData.loading},
                        density: dataGridDensity,
                    }}
                />
            </Box>
            <UpsertRegionRateDialog
                open={upsertDialogState.open}
                close={() => setUpsertDialogState(initUpsertDialogState)}
                data={upsertDialogState.data}
                onSuccess={onRegionRateUpserted}
            />
        </>
    );
};

export default RegionRatesSection;
